import {
	Popover,
	PopoverButton,
	PopoverPanel,
	PopoverPanelProps,
	useClose,
} from "@headlessui/react";
import { useEffect } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

type MenuProps = {
	testId?: string;
	/** Sets `aria-label` on the menu trigger (use when the button is icon-only). */
	ariaLabel?: string;
	buttonClassName?: string;
	button: React.ReactNode;
	panelClassName?: string;
	children: React.ReactNode;
	anchor: PopoverPanelProps["anchor"];
	collapsed?: boolean;
	trackingId?: string;
};

// The panel is portaled and floats, so it won't disappear with the trigger when an
// ancestor collapses. Close it explicitly. Rendered inside Popover so useClose resolves.
const CloseOnCollapse = ({ collapsed }: { collapsed?: boolean }) => {
	const close = useClose();
	useEffect(() => {
		if (collapsed) close();
	}, [collapsed, close]);
	return null;
};

export const Menu = ({
	testId,
	ariaLabel,
	buttonClassName,
	button,
	panelClassName,
	anchor,
	children,
	collapsed,
	trackingId,
}: MenuProps) => {
	return (
		<Popover className="group">
			{collapsed !== undefined && <CloseOnCollapse collapsed={collapsed} />}
			<PopoverButton
				aria-label={ariaLabel}
				data-testid={testId}
				{...trackingAttrs(trackingId)}
				className={cn("outline-0", buttonClassName)}
			>
				{button}
			</PopoverButton>
			<PopoverPanel
				anchor={anchor}
				className={cn(
					"z-[200000] min-w-fit min-h-fit shadow-md shadow-gray-900/15 bg-transparent rounded-md",
					panelClassName,
				)}
			>
				{children}
			</PopoverPanel>
		</Popover>
	);
};
